import { motion } from "framer-motion";
import { Home, Building2, Car, Check, MessageCircle } from "lucide-react";
import { waLink } from "../config/business";
import { useLanguage } from "../i18n/LanguageContext";
import { Chapter } from "./shared";

const META = {
  home: { Icon: Home, img: "/images/real-tools.webp", alt: "UNLockSmiths professional tool cases ready for a home lockout job" },
  business: { Icon: Building2, img: "/images/real-brand-wide.webp", alt: "UNLockSmiths 24/7 emergency locksmith service" },
  auto: { Icon: Car, img: "/images/real-car.webp", alt: "UNLockSmiths branded car used for car lockout call-outs in Edinburgh" },
};

const Services = () => {
  const { t } = useLanguage();

  return (
    <section id="services" data-testid="services-section" className="relative mx-auto max-w-6xl px-5 sm:px-8 py-20 sm:py-28">
      <Chapter title={t.services.title} sub={t.services.sub} />

      <div className="grid gap-6 sm:gap-7 md:grid-cols-3">
        {t.services.categories.map((c, i) => {
          const { Icon, img, alt } = META[c.id];
          return (
            <motion.article
              key={c.id}
              data-testid={`service-card-${c.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.65, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -8 }}
              className="card-elevated group flex flex-col overflow-hidden rounded-3xl"
            >
              <div className="relative h-44 sm:h-48 overflow-hidden">
                <img src={img} alt={alt} loading="lazy" className="h-full w-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.06]" />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-card)] via-[var(--bg-card)]/30 to-transparent" />
                <span className="icon-tile absolute bottom-4 left-6 flex h-12 w-12 items-center justify-center rounded-2xl text-amber-400">
                  <Icon className="h-6 w-6" strokeWidth={2} />
                </span>
              </div>
              <div className="flex flex-1 flex-col p-7 sm:p-8 pt-5">
                <h3 className="font-heading text-xl sm:text-2xl font-semibold tracking-[-0.02em]">{c.title}</h3>
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-slate-400">{c.desc}</p>
                <ul className="mt-6 space-y-2.5" data-testid={`service-list-${c.id}`}>
                  {c.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm sm:text-[15px] text-slate-200">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" strokeWidth={2.5} />
                      {item}
                    </li>
                  ))}
                </ul>
                <a
                  data-testid={`service-book-${c.id}`}
                  href={waLink(`${t.contact.waMessage}${c.title}. `)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-wa mt-8 w-full rounded-2xl px-5 py-3.5 text-sm"
                >
                  <MessageCircle className="h-4 w-4" strokeWidth={2.5} />
                  {t.services.book}
                </a>
              </div>
            </motion.article>
          );
        })}
      </div>
    </section>
  );
};

export default Services;
